import type { ResolvedTreeConfig } from './tree-config'

export interface TreeRowMeta {
  _treeLevel: number
  _treeExpanded: boolean
  _treeHasChild: boolean
  _treeLeaf: boolean
  /** 祖先层级是否延续竖线（length = level） */
  _treeLines: boolean[]
  /** 是否为同级最后一个节点 */
  _treeLast: boolean
}

export type TreeFlatRow = Record<string, unknown> & TreeRowMeta

function isRootParent(
  parentId: unknown,
  rootParentValue: unknown
) {
  return parentId === rootParentValue
}

function buildChildrenMap(
  flatSource: Record<string, unknown>[],
  config: ResolvedTreeConfig
) {
  const childrenMap = new Map<string, Record<string, unknown>[]>()
  const roots: Record<string, unknown>[] = []

  for (const row of flatSource) {
    const parentId = row[config.parentField]
    if (isRootParent(parentId, config.rootParentValue)) {
      roots.push(row)
      continue
    }
    const key = String(parentId)
    const list = childrenMap.get(key)
    if (list) {
      list.push(row)
    } else {
      childrenMap.set(key, [row])
    }
  }

  return { roots, childrenMap }
}

/** 拍平数据 + 展开态 → 虚拟列表可见行 */
export function flattenVisibleFromFlat(
  flatSource: Record<string, unknown>[],
  expandSet: Set<string>,
  config: ResolvedTreeConfig
): TreeFlatRow[] {
  const { roots, childrenMap } = buildChildrenMap(flatSource, config)
  const result: TreeFlatRow[] = []

  const walk = (
    row: Record<string, unknown>,
    level: number,
    lines: boolean[],
    isLast: boolean
  ) => {
    const id = String(row[config.rowField])
    const children = childrenMap.get(id) ?? []
    const hasChild = children.length > 0
    const expanded = expandSet.has(id)

    result.push({
      ...row,
      _treeLevel: level,
      _treeExpanded: expanded,
      _treeHasChild: hasChild,
      _treeLeaf: !hasChild,
      _treeLines: [...lines],
      _treeLast: isLast,
    })

    if (hasChild && expanded) {
      children.forEach((child, index) => {
        const childIsLast = index === children.length - 1
        walk(child, level + 1, [...lines, !isLast], childIsLast)
      })
    }
  }

  roots.forEach((root, index) => {
    walk(root, 0, [], index === roots.length - 1)
  })

  return result
}

export function collectExpandableIds(
  flatSource: Record<string, unknown>[],
  config: ResolvedTreeConfig
) {
  const childParentIds = new Set<string>()
  for (const row of flatSource) {
    const parentId = row[config.parentField]
    if (!isRootParent(parentId, config.rootParentValue)) {
      childParentIds.add(String(parentId))
    }
  }
  return childParentIds
}

export function hasTreeChildren(
  row: Record<string, unknown>,
  flatSource: Record<string, unknown>[],
  config: ResolvedTreeConfig
) {
  const id = row[config.rowField]
  return flatSource.some(
    (item) => item[config.parentField] === id
  )
}
